/**
 * Marketing Consent API - Handles customer marketing preferences
 * Optimized version with improved error handling, security, and performance
 */
class MarketingConsentAPI {
  constructor() {
    this.emailToggle = document.getElementById('marketingEmail');
    this.smsToggle = document.getElementById('marketingSMS');
    this.endpoints = {
      email: '/apps/api/customers/email-marketing-consent',
      sms: '/apps/api/customers/sms-marketing-consent'
    };
    
    this.init();
  }

  init() {
    this.loadCurrentConsent();
    this.bindEvents();
  }

  bindEvents() {
    if (this.emailToggle) {
      this.emailToggle.addEventListener('change', (e) => {
        this.handleToggleChange('email', e.target.checked);
      });
    }

    if (this.smsToggle) {
      this.smsToggle.addEventListener('change', (e) => {
        this.handleToggleChange('sms', e.target.checked);
      });
    }
  }

  async loadCurrentConsent() {
    // Set a timeout for the API call
    const timeoutDuration = 10000; // 10 seconds
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout')), timeoutDuration);
    });

    // Show loading states
    this.showInitialLoadingState();

    try {
      const response = await Promise.race([
        fetch('/apps/api/customers/marketing-consent', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }),
        timeoutPromise
      ]);

      const consentData = {};

      if (response.ok) {
        const responseData = await response.json();
        
        // Handle the actual API response structure with customer object
        if (responseData.customer) {
          // Email marketing consent
          if (responseData.customer.emailMarketingConsent) {
            consentData.email = responseData.customer.emailMarketingConsent.marketingState === 'SUBSCRIBED';
          } else {
            consentData.email = false; // Default to false if no consent data
          }
          
          // SMS marketing consent - handle null case
          if (responseData.customer.smsMarketingConsent) {
            consentData.sms = responseData.customer.smsMarketingConsent.marketingState === 'SUBSCRIBED';
          } else {
            consentData.sms = false; // Default to false if null or no consent data
          }
        }
      } else {
        // If API call fails, show error notification
        console.error('Failed to load consent status:', response.status, response.statusText);
      }

      this.updateToggleStates(consentData);
    } catch (error) {
      console.error('Error loading current consent status:', error);
      
      // Show appropriate error message based on error type
      if (error.message === 'Request timeout') {
        this.showNotification('Loading preferences is taking longer than expected. Please refresh the page.', 'error');
      } else {
        this.showNotification('Unable to load current preferences. Please refresh the page.', 'error');
      }
    } finally {
      // Always remove skeleton and toggle loading states after API call completes
      this.removeAllSkeletonLoading();
      this.removeInitialToggleLoading();
    }
  }

  updateToggleStates(consentData) {
    if (this.emailToggle && consentData.hasOwnProperty('email')) {
      this.emailToggle.checked = consentData.email;
      this.updateSliderState(this.emailToggle, consentData.email);
    }

    if (this.smsToggle && consentData.hasOwnProperty('sms')) {
      this.smsToggle.checked = consentData.sms;
      this.updateSliderState(this.smsToggle, consentData.sms);
    }
  }

  updateSliderState(toggle, isChecked) {
    const slider = toggle.nextElementSibling;
    if (slider) {
      slider.setAttribute('aria-checked', isChecked.toString());
    }
  }

  async handleToggleChange(type, isEnabled) {
    const toggle = type === 'email' ? this.emailToggle : this.smsToggle;
    
    // Disable the toggle during the request
    this.setToggleDisabled(toggle, true);
    
    // Optimistically update UI
    this.updateSliderState(toggle, isEnabled);
    
    // Show loading state
    this.setToggleLoading(toggle, true);

    try {
      const response = await this.updateConsent(type, isEnabled);
      
      // Check if the response contains the expected data structure
      const consentKey = type === 'email' ? 'emailMarketingConsent' : 'smsMarketingConsent';
      const hasCustomerData = response.customer && response.customer[consentKey];
      const isSuccess = hasCustomerData && response.customer[consentKey].marketingState === (isEnabled ? 'SUBSCRIBED' : 'UNSUBSCRIBED');
      
      if (isSuccess) {
        this.showSuccessMessage(type, isEnabled);
      } else {
        // Revert on failure
        toggle.checked = !isEnabled;
        this.updateSliderState(toggle, !isEnabled);
        this.showErrorMessage(type, 'Failed to update preference');
      }
    } catch (error) {
      // Revert on error
      toggle.checked = !isEnabled;
      this.updateSliderState(toggle, !isEnabled);
      this.showErrorMessage(type, 'Please try again.');
      console.error(`Error updating ${type} consent:`, error);
    } finally {
      // Re-enable the toggle and remove loading state
      this.setToggleLoading(toggle, false);
      this.setToggleDisabled(toggle, false);
    }
  }

  async updateConsent(type, isEnabled) {
    const endpoint = this.endpoints[type];
    
    // Prepare the request body based on the API documentation requirements
    const requestBody = {
      [type === 'email' ? 'emailMarketingConsent' : 'smsMarketingConsent']: {
        marketingState: isEnabled ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
        consentUpdatedAt: new Date().toISOString()
      }
    };
    
    // Add marketingOptInLevel for SMS requests as it's required
    if (type === 'sms') {
      requestBody.smsMarketingConsent.marketingOptInLevel = 'SINGLE_OPT_IN';
    }
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const responseData = await response.json();
    console.log('API Response:', responseData);
    
    return responseData;
  }

  setToggleLoading(toggle, isLoading) {
    const slider = toggle.nextElementSibling;
    if (slider) {
      if (isLoading) {
        slider.classList.add('loading');
        slider.setAttribute('aria-busy', 'true');
      } else {
        slider.classList.remove('loading');
        slider.setAttribute('aria-busy', 'false');
      }
    }
  }

  setToggleDisabled(toggle, isDisabled) {
    // Disable/enable the input element
    toggle.disabled = isDisabled;
    
    // Update the label class
    const switchLabel = toggle.closest('.switch');
    if (switchLabel) {
      if (isDisabled) {
        switchLabel.classList.add('disabled');
      } else {
        switchLabel.classList.remove('disabled');
      }
    }
    
    // Update the slider aria-disabled attribute
    const slider = toggle.nextElementSibling;
    if (slider) {
      slider.setAttribute('aria-disabled', isDisabled.toString());
    }
  }

  showSuccessMessage(type, isEnabled) {
    const message = isEnabled 
      ? `You're now subscribed to ${type} updates!`
      : `You've unsubscribed from ${type} updates.`;
    
    this.showNotification(message, 'success');
  }

  showErrorMessage(type, message) {
    this.showNotification(`Failed to update ${type} preference. ${message}`, 'error');
  }

  showNotification(message, type = 'info') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `consent-notification consent-notification--${type}`;
    notification.setAttribute('role', 'alert');
    notification.setAttribute('aria-live', 'polite');
    
    notification.innerHTML = `
      <div class="notification-content">
        <span class="notification-message">${message}</span>
        <button class="notification-close" aria-label="Close notification">×</button>
      </div>
    `;

    document.body.appendChild(notification);

    // Function to remove notification with fade out
    const removeNotification = () => {
      notification.classList.add('fade-out');
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 300); // Match the CSS transition duration
    };

    // Auto-remove after 5 seconds
    setTimeout(removeNotification, 5000);

    // Close button functionality
    const closeBtn = notification.querySelector('.notification-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', removeNotification);
    }
  }

  // Public API methods
  getEmailConsent() {
    return this.emailToggle ? this.emailToggle.checked : false;
  }

  getSMSConsent() {
    return this.smsToggle ? this.smsToggle.checked : false;
  }

  setEmailConsent(enabled) {
    if (this.emailToggle) {
      this.emailToggle.checked = enabled;
      this.updateSliderState(this.emailToggle, enabled);
    }
  }

  setSMSConsent(enabled) {
    if (this.smsToggle) {
      this.smsToggle.checked = enabled;
      this.updateSliderState(this.smsToggle, enabled);
    }
  }

  // Batch update method
  async updateAllConsent(emailEnabled, smsEnabled) {
    const promises = [];
    
    if (this.emailToggle && this.emailToggle.checked !== emailEnabled) {
      promises.push(this.updateConsent('email', emailEnabled));
    }
    
    if (this.smsToggle && this.smsToggle.checked !== smsEnabled) {
      promises.push(this.updateConsent('sms', smsEnabled));
    }

    try {
      await Promise.all(promises);
      this.setEmailConsent(emailEnabled);
      this.setSMSConsent(smsEnabled);
      this.showNotification('Marketing preferences updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating all consent:', error);
      this.showNotification('Failed to update some preferences. Please try again.', 'error');
    }
  }

  // Get detailed consent information
  async getDetailedConsent() {
    try {
      const [emailResponse, smsResponse] = await Promise.all([
        fetch(this.endpoints.email, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }),
        fetch(this.endpoints.sms, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })
      ]);

      const emailData = emailResponse.ok ? await emailResponse.json() : null;
      const smsData = smsResponse.ok ? await smsResponse.json() : null;

      return {
        email: emailData?.emailMarketingConsent || null,
        sms: smsData?.smsMarketingConsent || null
      };
    } catch (error) {
      console.error('Error getting detailed consent:', error);
      return { email: null, sms: null };
    }
  }

  showInitialLoadingState() {
    // Show loading state for both toggles initially
    if (this.emailToggle) {
      this.setToggleLoading(this.emailToggle, true);
      this.setToggleDisabled(this.emailToggle, true);
    }
    
    if (this.smsToggle) {
      this.setToggleLoading(this.smsToggle, true);
      this.setToggleDisabled(this.smsToggle, true);
    }
  }

  hideInitialLoadingState() {
    // Hide loading state for both toggles
    if (this.emailToggle) {
      this.setToggleLoading(this.emailToggle, false);
      this.setToggleDisabled(this.emailToggle, false);
    }
    
    if (this.smsToggle) {
      this.setToggleLoading(this.smsToggle, false);
      this.setToggleDisabled(this.smsToggle, false);
    }
  }

  removeAllSkeletonLoading() {
    // Remove skeleton-loading class from both toggle containers
    const emailContainer = this.emailToggle?.closest('.toggle-container');
    const smsContainer = this.smsToggle?.closest('.toggle-container');
    const infoMessageContainer = document.querySelector('.info-message-container');
    
    if (emailContainer) {
      emailContainer.classList.remove('skeleton-loading');
    }
    
    if (smsContainer) {
      smsContainer.classList.remove('skeleton-loading');
    }
    
    if (infoMessageContainer) {
      infoMessageContainer.classList.remove('skeleton-loading');
    }
  }

  removeInitialToggleLoading() {
    // Remove loading state from toggle switches
    if (this.emailToggle) {
      this.setToggleLoading(this.emailToggle, false);
      this.setToggleDisabled(this.emailToggle, false);
    }
    
    if (this.smsToggle) {
      this.setToggleLoading(this.smsToggle, false);
      // Check if SMS toggle should remain disabled (no phone number)
      const shouldBeDisabled = this.smsToggle.getAttribute('data-originally-disabled') === 'true';
      this.setToggleDisabled(this.smsToggle, shouldBeDisabled);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.section-marketing-consent')) {
    window.marketingConsentAPI = new MarketingConsentAPI();
  }
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MarketingConsentAPI;
}
